import { z } from 'zod'

export const applicationStatusSchema = z.enum(['ACTIVE', 'ARCHIVED'])
export type ApplicationStatus = z.infer<typeof applicationStatusSchema>

const optionalText = z.string().nullish()

const rawApplicationSchema = z.looseObject({
  id: optionalText,
  applicationId: optionalText,
  name: optionalText,
  applicationName: optionalText,
  organizationId: optionalText,
  businessUnitId: optionalText,
  businessUnitName: optionalText,
  businessUnit: optionalText,
  projectId: optionalText,
  projectName: optionalText,
  teamName: optionalText,
  ownerName: optionalText,
  projectOwner: optionalText,
  ownerEmail: optionalText,
  applicationSme: optionalText,
  projectSME: optionalText,
  smeEmail: optionalText,
  projectSMEEmail: optionalText,
  projectDLEmail: optionalText,
  expectedGoLiveDate: optionalText,
  testerName: optionalText,
  testerEmail: optionalText,
  serviceNowGroupName: optionalText,
  serviceNowEmail: optionalText,
  consumerIds: z.unknown().optional(),
  consumerId: z.unknown().optional(),
  consumer_ids: z.unknown().optional(),
  consumers: z.unknown().optional(),
  consumerCount: z.number().int().nonnegative().nullish(),
  status: optionalText,
  createdBy: optionalText,
  createdAt: optionalText,
  updatedBy: optionalText,
  updatedAt: optionalText,
})

export const applicationSchema = rawApplicationSchema.transform((raw, ctx) => {
  const recordId = raw.id?.trim() || raw.applicationId?.trim()
  const applicationId = raw.applicationId?.trim() || raw.id?.trim()
  const name = raw.name?.trim() || raw.applicationName?.trim() || applicationId

  if (!recordId || !applicationId || !name) {
    ctx.addIssue({
      code: 'custom',
      message: 'Application identity is missing',
    })
    return z.NEVER
  }

  const consumerIds = normalizeConsumerIds(
    raw.consumerIds,
    raw.consumerId,
    raw.consumer_ids,
    raw.consumers,
  )

  return {
    recordId,
    applicationId,
    name,
    organizationId: raw.organizationId?.trim() ?? '',
    businessUnitId: raw.businessUnitId?.trim() ?? '',
    businessUnit:
      raw.businessUnitName?.trim() || raw.businessUnit?.trim() || '',
    projectId: raw.projectId?.trim() ?? '',
    projectName: raw.projectName?.trim() || raw.teamName?.trim() || '',
    ownerName: raw.ownerName?.trim() || raw.projectOwner?.trim() || '',
    ownerEmail: raw.ownerEmail?.trim() ?? '',
    smeName: raw.applicationSme?.trim() || raw.projectSME?.trim() || '',
    smeEmail: raw.smeEmail?.trim() || raw.projectSMEEmail?.trim() || '',
    distributionEmail: raw.projectDLEmail?.trim() ?? '',
    expectedGoLiveDate: raw.expectedGoLiveDate?.trim() ?? '',
    testerName: raw.testerName?.trim() ?? '',
    testerEmail: raw.testerEmail?.trim() ?? '',
    serviceNowGroup: raw.serviceNowGroupName?.trim() ?? '',
    serviceNowEmail: raw.serviceNowEmail?.trim() ?? '',
    consumerIds,
    consumerCount: raw.consumerCount ?? consumerIds.length,
    status: raw.status?.toUpperCase() === 'ARCHIVED' ? 'ARCHIVED' : 'ACTIVE',
    createdBy: raw.createdBy?.trim() ?? '',
    createdAt: raw.createdAt?.trim() ?? '',
    updatedBy: raw.updatedBy?.trim() ?? '',
    updatedAt: raw.updatedAt?.trim() ?? '',
  } satisfies Application
})

function normalizeConsumerIds(...sources: unknown[]): string[] {
  const ids = new Set<string>()

  const add = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(add)
      return
    }
    if (typeof value === 'string') {
      const id = value.trim()
      if (id) ids.add(id)
      return
    }
    if (!value || typeof value !== 'object') return

    const record = value as Record<string, unknown>
    add(record.id)
    add(record.consumerId)
    add(record.consumer_id)
  }

  sources.forEach(add)
  return [...ids]
}

export type Application = {
  recordId: string
  applicationId: string
  name: string
  organizationId: string
  businessUnitId: string
  businessUnit: string
  projectId: string
  projectName: string
  ownerName: string
  ownerEmail: string
  smeName: string
  smeEmail: string
  distributionEmail: string
  expectedGoLiveDate: string
  testerName: string
  testerEmail: string
  serviceNowGroup: string
  serviceNowEmail: string
  consumerIds: string[]
  consumerCount: number
  status: ApplicationStatus
  createdBy: string
  createdAt: string
  updatedBy: string
  updatedAt: string
}

export interface ApplicationPage {
  items: Application[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface OnboardingHierarchyOption {
  id: string
  name: string
  code: string
}

export interface OnboardingProject extends OnboardingHierarchyOption {
  ownerName: string
  ownerEmail: string
  distributionEmail: string
  expectedGoLiveDate: string
}

const hierarchyOptionSchema = z.looseObject({
  id: z.string(),
  name: optionalText,
  businessUnitName: optionalText,
  projectName: optionalText,
  code: optionalText,
})

const onboardingProjectSchema = hierarchyOptionSchema.extend({
  ownerName: optionalText,
  ownerEmail: optionalText,
  projectDlEmail: optionalText,
  projectDLEmail: optionalText,
  expectedGoLiveDate: optionalText,
})

export function parseOnboardingHierarchyOptions(
  payload: unknown,
): OnboardingHierarchyOption[] {
  const envelope = collectionEnvelopeSchema.parse(payload)
  const nested = pageContainerSchema.safeParse(envelope.data)
  const rawItems = Array.isArray(envelope.data)
    ? envelope.data
    : nested.success
      ? (nested.data.content ?? nested.data.items ?? [])
      : []

  return hierarchyOptionSchema
    .array()
    .parse(rawItems)
    .map((item) => ({
      id: item.id,
      name:
        item.name?.trim() ||
        item.businessUnitName?.trim() ||
        item.projectName?.trim() ||
        item.id,
      code: item.code?.trim() ?? '',
    }))
}

export function parseOnboardingProjects(payload: unknown): OnboardingProject[] {
  const envelope = collectionEnvelopeSchema.parse(payload)
  const nested = pageContainerSchema.safeParse(envelope.data)
  const rawItems = Array.isArray(envelope.data)
    ? envelope.data
    : nested.success
      ? (nested.data.content ?? nested.data.items ?? [])
      : []

  return onboardingProjectSchema
    .array()
    .parse(rawItems)
    .map((project) => ({
      id: project.id,
      name: project.name?.trim() || project.projectName?.trim() || project.id,
      code: project.code?.trim() ?? '',
      ownerName: project.ownerName?.trim() ?? '',
      ownerEmail: project.ownerEmail?.trim() ?? '',
      distributionEmail:
        project.projectDlEmail?.trim() || project.projectDLEmail?.trim() || '',
      expectedGoLiveDate: project.expectedGoLiveDate?.trim() ?? '',
    }))
}

const pageMetaSchema = z.looseObject({
  page: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
  totalElements: z.number().int().nonnegative().optional(),
  totalPages: z.number().int().nonnegative().optional(),
})

const pageContainerSchema = z.looseObject({
  content: z.array(z.unknown()).optional(),
  items: z.array(z.unknown()).optional(),
  page: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
  totalElements: z.number().int().nonnegative().optional(),
  totalPages: z.number().int().nonnegative().optional(),
})

const collectionEnvelopeSchema = z.looseObject({
  data: z.unknown(),
  meta: pageMetaSchema.optional(),
})

export function parseApplicationPage(
  payload: unknown,
  fallback: { page: number; size: number },
): ApplicationPage {
  const envelope = collectionEnvelopeSchema.parse(payload)
  const nested = pageContainerSchema.safeParse(envelope.data)
  const rawItems = Array.isArray(envelope.data)
    ? envelope.data
    : nested.success
      ? (nested.data.content ?? nested.data.items ?? [])
      : []
  const items = applicationSchema.array().parse(rawItems)
  const pageData = nested.success ? nested.data : {}
  const meta = envelope.meta ?? pageData

  return {
    items,
    page: meta.page ?? fallback.page,
    size: meta.size ?? fallback.size,
    totalElements: meta.totalElements ?? items.length,
    totalPages: meta.totalPages ?? (items.length > 0 ? 1 : 0),
  }
}

export function parseApplication(payload: unknown): Application {
  if (payload !== null && typeof payload === 'object' && 'data' in payload) {
    return applicationSchema.parse((payload as { data: unknown }).data)
  }
  return applicationSchema.parse(payload)
}
